import * as Sentry from "@sentry/react";
import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { subscribeWithSelector } from "zustand/middleware";
import type { Session } from "@supabase/supabase-js";
import supabase from "@/lib/__shared__/supabase/client";

export type ModalIds = "INTERRUPTION_MODAL" | "UPGRADE_MODAL" | null;

export type Subscription = {
	id: string;
	status: "active" | "canceled" | "past_due" | "trialing" | "unpaid";
	interval: "MONTH" | "YEAR" | "PROCESSING" | null;
	start_date: string;
	end_date: string;
};

type AppState = {
	isAuthLoading: boolean;
	isUserSubscribed: boolean;
	isUserEngaged: boolean;
	openedModalId: ModalIds;
	session: Session | null;
	subscription: Subscription | null;
};

type AppActions = {
	setAuthLoading: (isLoading: boolean) => void;
	setSession: (session: Session | null) => void;
	setSubscription: (subscription: Subscription | null) => void;
	setUserEngagementStatus: (isEngaged: boolean) => void;
	showModal: (id: ModalIds, isVisible: boolean) => void;
};

export type AppStoreProps = AppState & AppActions;

const checkForActiveSubscription = (subscription: Subscription | null) => {
	if (!subscription) return false;

	// Check if the startDate and endDate are between the current date
	const currentDate = new Date();
	const startDate = new Date(subscription.start_date);
	const endDate = new Date(subscription.end_date);

	// TODO: we need to handle updates for when the dates are outside of the current date range
	// and the status is still active or trialing

	return currentDate >= startDate && currentDate <= endDate;
};

export const appStore = createStore<AppStoreProps>()(
	subscribeWithSelector(
		immer((set) => ({
			isAuthLoading: false,
			isUserSubscribed: false,
			isUserEngaged: false,
			openedModalId: null,
			session: null,
			subscription: null,
			setAuthLoading: (isLoading: boolean) =>
				set((state) => {
					state.isAuthLoading = isLoading;
				}),
			setSession: async (session: Session | null) => {
				if (!session) {
					set((state) => {
						state.session = null;
						state.isUserSubscribed = false;
						state.subscription = null;
					});
				} else if (session.user) {
					// Fetch user subscription data
					const { data, error: subscriptionError } = await supabase
						.from("user_meta_data")
						.select("subscription")
						.eq("id", session.user.id)
						.single();

					if (subscriptionError) {
						Sentry.captureException(subscriptionError);
						set((state) => {
							state.session = session;
							state.isUserSubscribed = false;
							state.subscription = null;
						});
					} else {
						set((state) => {
							state.session = session;
							state.subscription = data.subscription;
							state.isUserSubscribed = checkForActiveSubscription(
								data.subscription,
							);
						});
					}
				}
			},
			setSubscription: (subscription: Subscription | null) =>
				set((state) => {
					state.subscription = subscription;
					state.isUserSubscribed = checkForActiveSubscription(subscription);
				}),
			setUserEngagementStatus: (isEngaged: boolean) =>
				set((state) => {
					state.isUserEngaged = isEngaged;
				}),
			showModal: (id: ModalIds, isVisible: boolean) =>
				set((state) => {
					if (isVisible) {
						state.openedModalId = id;
					} else {
						state.openedModalId = null;
					}
				}),
		})),
	),
);

export type AppStore = typeof appStore;

export const useAppStore = () => {
	const store = useStore(appStore);
	return store;
};
